// Pure helpers for the local session-draft backup: key scheme + expiry.
// One draft slot per USER (not per session) — sessionId is regenerated on
// every page load (useState(uuidv4()) in InfiniteCanvas.js, never persisted),
// so a per-session key would never be rediscovered after the exact reload
// this feature exists to protect against. The draft payload embeds its own
// sessionId; restoring re-adopts it.

export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export function draftKeyFor(userId) {
  return `sg_draft_${userId}`;
}

export function isDraftExpired(savedAt, now = Date.now()) {
  return now - savedAt >= DRAFT_TTL_MS;
}

// Does a draft/canvas snapshot hold anything worth offering to restore?
//
// The draft feature protects real unsaved work against an accidental
// reload/logout. A PRISTINE canvas — the initial card deck still face-down,
// deck not yet revealed, no notes/edges/chat/brief — is not work; persisting
// it and prompting to "restore" it on every visit is pure noise. (Its
// serialized shape also isn't byte-stable across loads, so no content
// fingerprint could reliably suppress the re-prompt — that was the root
// cause of the discard loop this replaces.) Meaningful = the user engaged
// with the canvas: deck revealed, or any non-card node / edge / brainstorm
// entry / chat message / non-empty brief / scenari_futuri branch work.
export function draftHasMeaningfulContent(snap) {
  if (!snap) return false;
  if (snap.deckRevealed) return true;
  const nodes = Array.isArray(snap.nodes) ? snap.nodes : [];
  if (nodes.some((n) => n && n.type !== 'customCardNode')) return true;
  if ((snap.edges || []).length) return true;
  if ((snap.brainstorm || []).length) return true;
  if ((snap.messages || []).length) return true;
  // scenari_futuri mode: a user can build out an entire branching timeline
  // (root -> splits -> branches) before ever revealing the deck or sending a
  // chat message — without this check that work reads as a pristine canvas
  // and the autosave silently drops it (found live-verifying Task 2).
  if ((snap.scenarioSplits || []).length) return true;
  if ((snap.scenarioBranches || []).length) return true;
  const b = snap.brief;
  if (b && (b.obiettivo || b.direzione || b.sintesi_processo ||
            (b.evidenze_chiave || []).length || (b.carte_collegate || []).length ||
            (b.decisions || []).length)) {
    return true;
  }
  return false;
}
